import React, { useState, useEffect } from 'react';
import { Stack, Paper, Title, Button, Text, Group, Loader } from '@mantine/core';
import { IconChartBar, IconRefresh } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

export default function AdminSeoAuditTab({ user, apiBaseUrl, setActiveTab }) {
  const [status, setStatus] = useState(null);
  const [reportTimestamp, setReportTimestamp] = useState(Date.now());
  const { t } = useTranslation();

  const isAdmin = user?.email?.toLowerCase() === 'office@ailinkb.shop' || user?.role?.includes('ADMIN');

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'OPEN_CRM') {
        if (setActiveTab) setActiveTab('leads');
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [setActiveTab]);

  const fetchStatus = () => {
    fetch(`${apiBaseUrl}/api/admin/seo-audit/status?adminEmail=${user.email}`)
      .then(res => res.json())
      .then(data => {
        setStatus(prev => {
          // If scanning just finished, update the report timestamp to reload iframe
          if (prev?.isScanning && !data.isScanning) {
            setReportTimestamp(Date.now());
          }
          return data;
        });
      })
      .catch(console.error);
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 3000);
    return () => clearInterval(interval);
  }, []);

  const triggerAudit = (mode) => {
    fetch(`${apiBaseUrl}/api/admin/seo-audit/trigger`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ adminEmail: user.email, mode: mode })
    })
      .then(res => res.json())
      .then(() => fetchStatus())
      .catch(console.error);
  };

  return (
    <Stack gap="md">
      <Paper p="md" withBorder shadow="sm" radius="md">
        <Group justify="space-between" align="center">
          <div>
            <Title order={3}>{t('seo_audit_title')}</Title>
            <Text size="sm" c="dimmed">{t('seo_audit_desc')}</Text>
          </div>
          {isAdmin && (
            <Group>
              <Button 
                leftSection={status?.isScanning ? <Loader size="xs" color="white" /> : <IconRefresh size={16} />} 
                onClick={() => triggerAudit('resume')} 
                disabled={status?.isScanning}
                color="indigo"
                variant="light"
              >
                {status?.isScanning ? `${t('seo_audit_scanning')} ${status.progress}% (${status.scannedCount}/${status.totalToScan})` : t('seo_audit_resume')}
              </Button>
              <Button 
                leftSection={status?.isScanning ? <Loader size="xs" color="white" /> : <IconRefresh size={16} />} 
                onClick={() => {
                  if(window.confirm(t('seo_audit_confirm'))) {
                    triggerAudit('new');
                  }
                }} 
                disabled={status?.isScanning}
                color="indigo"
              >
                {t('seo_audit_new')}
              </Button>
            </Group>
          )}
        </Group>
      </Paper>

      <Paper withBorder shadow="sm" radius="md" style={{ height: '800px', overflow: 'hidden' }}>
        <iframe 
          src={`${apiBaseUrl}/api/admin/seo-audit/report.html?adminEmail=${user.email}&lang=${user.language || 'PL'}&t=${reportTimestamp}`} 
          style={{ width: '100%', height: '100%', border: 'none' }} 
          title="SEO Report"
        />
      </Paper>
    </Stack>
  );
}

